import { useState } from 'react';
import type { CSSProperties } from 'react';
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Segmented,
  Skeleton,
  Space,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useT } from '../../../locales';
import {
  getCanonicalPermissionLabel,
  getFallbackPermissionActionKey,
  getPermissionModuleAnchorId,
  getPermissionModuleCategory,
  getPermissionModuleMetadata,
  getPermissionModuleSortRank,
  getPermissionSortRank,
  humanizePermissionPart,
  isDestructivePermissionAction,
  splitPermissionName,
  ROLE_PERMISSION_CATEGORIES,
  ROLE_PERMISSION_CATEGORY_LABEL_KEYS,
  type PermissionParts,
  type RolePermissionCategory,
} from '../permission-metadata';
import '../roles.css';

const PERMISSION_FILTER = {
  ALL: 'all',
  ASSIGNED: 'assigned',
  UNASSIGNED: 'unassigned',
} as const;

type PermissionFilter = (typeof PERMISSION_FILTER)[keyof typeof PERMISSION_FILTER];

export interface PermissionItem {
  id: string;
  name: string;
  description?: string | null;
}

export interface PermissionCheckboxListProps {
  value?: string[];
  onChange?: (selectedIds: string[]) => void;
  allPermissions: PermissionItem[];
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  readOnly?: boolean;
}

interface PermissionRowViewModel extends PermissionItem {
  action: string;
  label: string;
  helpText?: string;
}

interface PermissionModuleViewModel {
  resource: string;
  label: string;
  description?: string;
  category: RolePermissionCategory;
  permissions: PermissionRowViewModel[];
  selectedCount: number;
  totalCount: number;
  firstIndex: number;
}

function normalizeSearchValue(value: string): string {
  return value
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function uniquePermissions(permissions: PermissionItem[]): PermissionItem[] {
  const seenIds = new Set<string>();
  const unique: PermissionItem[] = [];

  for (const permission of permissions) {
    if (seenIds.has(permission.id)) continue;
    seenIds.add(permission.id);
    unique.push(permission);
  }

  return unique;
}

function getUsefulPermissionDescription(permission: PermissionItem, parts: PermissionParts) {
  const description = permission.description?.trim();
  if (!description) return undefined;

  // Known permissions already have business-facing copy in the shared
  // catalogue. The API description for these records is often only the
  // technical resource name, so showing it as help would be misleading.
  if (getCanonicalPermissionLabel(permission.name)) return undefined;

  const normalizedDescription = normalizeSearchValue(description);
  const genericValues = [permission.name, parts.resource, parts.action].map(normalizeSearchValue);
  return genericValues.includes(normalizedDescription) ? undefined : description;
}

function getPermissionLabel(
  permission: PermissionItem,
  action: string,
  t: ReturnType<typeof useT>,
) {
  const canonicalLabel = getCanonicalPermissionLabel(permission.name);
  if (canonicalLabel) return canonicalLabel;

  const fallbackActionKey = getFallbackPermissionActionKey(action);
  return fallbackActionKey ? t(fallbackActionKey) : humanizePermissionPart(action);
}

function buildModuleViewModels(
  permissions: PermissionItem[],
  selectedIds: ReadonlySet<string>,
  search: string,
  filter: PermissionFilter,
  t: ReturnType<typeof useT>,
): PermissionModuleViewModel[] {
  const moduleMap = new Map<string, { permissions: PermissionItem[]; firstIndex: number }>();

  permissions.forEach((permission, index) => {
    const { resource } = splitPermissionName(permission.name);
    const current = moduleMap.get(resource);
    if (current) {
      current.permissions.push(permission);
      return;
    }
    moduleMap.set(resource, { permissions: [permission], firstIndex: index });
  });

  const normalizedSearch = normalizeSearchValue(search.trim());
  const modules: PermissionModuleViewModel[] = [];

  for (const [resource, module] of moduleMap) {
    const metadata = getPermissionModuleMetadata(resource);
    const moduleLabel = metadata ? t(metadata.labelKey) : humanizePermissionPart(resource);
    const moduleDescription = metadata?.descriptionKey ? t(metadata.descriptionKey) : undefined;

    const sortedPermissions = module.permissions
      .map((permission, index) => ({ permission, index }))
      .sort((a, b) => {
        const rankDifference =
          getPermissionSortRank(a.permission.name) - getPermissionSortRank(b.permission.name);
        if (rankDifference !== 0) return rankDifference;

        const actionDifference = splitPermissionName(a.permission.name).action.localeCompare(
          splitPermissionName(b.permission.name).action,
        );
        return actionDifference || a.index - b.index;
      })
      .map(({ permission }) => permission);

    const visiblePermissions: PermissionRowViewModel[] = [];
    for (const permission of sortedPermissions) {
      const parts = splitPermissionName(permission.name);
      const isAssigned = selectedIds.has(permission.id);
      const matchesFilter =
        filter === PERMISSION_FILTER.ALL ||
        (filter === PERMISSION_FILTER.ASSIGNED && isAssigned) ||
        (filter === PERMISSION_FILTER.UNASSIGNED && !isAssigned);
      if (!matchesFilter) continue;

      const label = getPermissionLabel(permission, parts.action, t);
      const helpText = getUsefulPermissionDescription(permission, parts);
      const searchValues = [
        moduleLabel,
        resource,
        permission.name,
        label,
        permission.description ?? '',
        moduleDescription ?? '',
        parts.action,
      ];
      const matchesSearch =
        normalizedSearch.length === 0 ||
        searchValues.some((value) => normalizeSearchValue(value).includes(normalizedSearch));
      if (!matchesSearch) continue;

      visiblePermissions.push({ ...permission, action: parts.action, label, helpText });
    }

    if (visiblePermissions.length === 0) continue;

    modules.push({
      resource,
      label: moduleLabel,
      description: moduleDescription,
      category: getPermissionModuleCategory(resource),
      permissions: visiblePermissions,
      selectedCount: module.permissions.filter((permission) => selectedIds.has(permission.id))
        .length,
      totalCount: module.permissions.length,
      firstIndex: module.firstIndex,
    });
  }

  return modules.sort((a, b) => {
    const rankDifference =
      getPermissionModuleSortRank(a.resource) - getPermissionModuleSortRank(b.resource);
    if (rankDifference !== 0) return rankDifference;

    const unknownModuleDifference = a.resource.localeCompare(b.resource);
    return unknownModuleDifference || a.firstIndex - b.firstIndex;
  });
}

function groupModulesByCategory(
  modules: PermissionModuleViewModel[],
): Map<RolePermissionCategory, PermissionModuleViewModel[]> {
  const groups = new Map<RolePermissionCategory, PermissionModuleViewModel[]>();

  for (const module of modules) {
    const categoryModules = groups.get(module.category) ?? [];
    categoryModules.push(module);
    groups.set(module.category, categoryModules);
  }

  return groups;
}

export function PermissionCheckboxList({
  value,
  onChange,
  allPermissions,
  loading = false,
  error = null,
  onRetry,
  readOnly = false,
}: PermissionCheckboxListProps) {
  const t = useT();
  const { token } = theme.useToken();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<PermissionFilter>(PERMISSION_FILTER.ALL);
  const permissions = uniquePermissions(allPermissions);
  const selectedIds = new Set(value ?? []);
  const selectedCount = permissions.filter((permission) => selectedIds.has(permission.id)).length;
  const allPermissionsSelected =
    selectedCount === permissions.length && selectedIds.size === permissions.length;
  const modules = buildModuleViewModels(permissions, selectedIds, search, filter, t);
  const modulesByCategory = groupModulesByCategory(modules);
  const editorStyle = {
    '--permission-editor-border': token.colorBorderSecondary,
    '--permission-editor-hover': token.colorFillTertiary,
    '--permission-editor-surface': token.colorBgContainer,
    '--permission-editor-text': token.colorText,
    '--permission-editor-secondary-text': token.colorTextSecondary,
  } as CSSProperties;

  const emitSelection = (nextIds: ReadonlySet<string>) => {
    onChange?.([...nextIds]);
  };

  const handlePermissionChange = (permissionId: string, checked: boolean) => {
    const nextIds = new Set(selectedIds);
    if (checked) {
      nextIds.add(permissionId);
    } else {
      nextIds.delete(permissionId);
    }
    emitSelection(nextIds);
  };

  const handleModuleChange = (resource: string, checked: boolean) => {
    const nextIds = new Set(selectedIds);
    const module = permissions.filter(
      (permission) => splitPermissionName(permission.name).resource === resource,
    );

    for (const permission of module) {
      if (checked) {
        nextIds.add(permission.id);
      } else {
        nextIds.delete(permission.id);
      }
    }

    emitSelection(nextIds);
  };

  const handleFilterChange = (nextFilter: string | number) => {
    if (
      nextFilter === PERMISSION_FILTER.ALL ||
      nextFilter === PERMISSION_FILTER.ASSIGNED ||
      nextFilter === PERMISSION_FILTER.UNASSIGNED
    ) {
      setFilter(nextFilter);
    }
  };

  if (loading) {
    return (
      <div aria-busy="true" aria-label={t('pages.roles.permissionsLoading')}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="permission-editor__empty" role="alert">
        <Empty description={t('pages.roles.permissionsLoadError')}>
          {onRetry && <Button onClick={onRetry}>{t('pages.roles.retryPermissions')}</Button>}
        </Empty>
      </div>
    );
  }

  if (permissions.length === 0) {
    return (
      <div className="permission-editor__empty">
        <Empty description={t('pages.roles.permissionsEmpty')} />
      </div>
    );
  }

  return (
    <div
      className="permission-editor"
      style={editorStyle}
      aria-label={t('pages.roles.permissionEditorTitle')}
    >
      <div className="permission-editor__layout">
        <nav
          className="permission-editor__navigation"
          aria-label={t('pages.roles.permissionNavigation')}
        >
          <Typography.Text strong>{t('pages.roles.permissionIndex')}</Typography.Text>
          <ul className="permission-editor__navigation-list">
            {ROLE_PERMISSION_CATEGORIES.map((category) => {
              const categoryModules = modulesByCategory.get(category);
              if (!categoryModules?.length) return null;

              return (
                <li key={category} className="permission-editor__navigation-group">
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {t(ROLE_PERMISSION_CATEGORY_LABEL_KEYS[category])}
                  </Typography.Text>
                  <ul className="permission-editor__navigation-sublist">
                    {categoryModules.map((module) => (
                      <li key={module.resource}>
                        <a
                          className="permission-editor__navigation-link"
                          href={`#${getPermissionModuleAnchorId(module.resource)}`}
                          aria-label={`${module.label}, ${t('pages.roles.moduleSelectionSummary', {
                            selected: module.selectedCount,
                            total: module.totalCount,
                          })}`}
                          onClick={(event) => {
                            event.preventDefault();
                            document
                              .getElementById(getPermissionModuleAnchorId(module.resource))
                              ?.scrollIntoView({ block: 'start' });
                          }}
                        >
                          <span className="permission-editor__navigation-label">
                            {module.label}
                          </span>
                          <span className="permission-editor__navigation-count" aria-hidden="true">
                            {module.selectedCount}/{module.totalCount}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="permission-editor__content">
          <div className="permission-editor__toolbar">
            <div className="permission-editor__toolbar-row">
              <div>
                <Typography.Text strong>{t('pages.roles.permissionEditorTitle')}</Typography.Text>
                <Typography.Text
                  type="secondary"
                  aria-live="polite"
                  style={{ display: 'block', marginTop: 4, fontSize: 12 }}
                >
                  {t('pages.roles.selectionSummary', {
                    selected: selectedCount,
                    total: permissions.length,
                  })}
                </Typography.Text>
              </div>
              <div className="permission-editor__toolbar-actions">
                <Button
                  onClick={() => onChange?.(permissions.map((permission) => permission.id))}
                  disabled={readOnly || allPermissionsSelected}
                >
                  {t('pages.roles.selectAllPermissions')}
                </Button>
                <Button
                  onClick={() => onChange?.([])}
                  disabled={readOnly || selectedIds.size === 0}
                >
                  {t('pages.roles.clearPermissions')}
                </Button>
              </div>
            </div>

            <div className="permission-editor__filters">
              <Input
                className="permission-editor__search"
                prefix={<SearchOutlined />}
                placeholder={t('pages.roles.searchPermissionsPlaceholder')}
                aria-label={t('pages.roles.searchPermissionsPlaceholder')}
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <Space.Compact>
                <Typography.Text
                  type="secondary"
                  style={{ alignSelf: 'center', fontSize: 12, marginInlineEnd: 2 }}
                >
                  {t('pages.roles.permissionFilterLabel')}
                </Typography.Text>
                <Segmented
                  aria-label={t('pages.roles.permissionFilterLabel')}
                  value={filter}
                  onChange={handleFilterChange}
                  options={[
                    { label: t('pages.roles.filterAll'), value: PERMISSION_FILTER.ALL },
                    { label: t('pages.roles.filterAssigned'), value: PERMISSION_FILTER.ASSIGNED },
                    {
                      label: t('pages.roles.filterUnassigned'),
                      value: PERMISSION_FILTER.UNASSIGNED,
                    },
                  ]}
                />
              </Space.Compact>
            </div>
          </div>

          {modules.length === 0 ? (
            <div className="permission-editor__empty">
              <Empty description={t('pages.roles.permissionsNoMatches')} />
            </div>
          ) : (
            ROLE_PERMISSION_CATEGORIES.map((category) => {
              const categoryModules = modulesByCategory.get(category);
              if (!categoryModules?.length) return null;

              return (
                <section
                  key={category}
                  className="permission-editor__category"
                  aria-labelledby={`permission-category-${category}`}
                >
                  <Typography.Title
                    level={5}
                    id={`permission-category-${category}`}
                    className="permission-editor__category-heading"
                  >
                    {t(ROLE_PERMISSION_CATEGORY_LABEL_KEYS[category])}
                  </Typography.Title>
                  {categoryModules.map((module) => {
                    const allSelected = module.selectedCount === module.totalCount;
                    const partiallySelected =
                      module.selectedCount > 0 && module.selectedCount < module.totalCount;
                    const moduleHeadingId = `${getPermissionModuleAnchorId(module.resource)}-heading`;

                    return (
                      <section
                        key={module.resource}
                        id={getPermissionModuleAnchorId(module.resource)}
                        className="permission-editor__module"
                        aria-labelledby={moduleHeadingId}
                      >
                        <div
                          className="permission-editor__module-heading"
                          style={{ borderColor: token.colorBorderSecondary }}
                        >
                          <div className="permission-editor__module-title">
                            <Typography.Title level={4} id={moduleHeadingId}>
                              {module.label}
                            </Typography.Title>
                            {module.description && (
                              <Typography.Text
                                type="secondary"
                                className="permission-editor__module-description"
                              >
                                {module.description}
                              </Typography.Text>
                            )}
                          </div>
                          <div className="permission-editor__module-controls">
                            <Checkbox
                              className="permission-editor__module-checkbox"
                              checked={allSelected}
                              indeterminate={partiallySelected}
                              disabled={readOnly}
                              aria-label={t('pages.roles.selectModuleAria', {
                                module: module.label,
                              })}
                              onChange={(event) =>
                                handleModuleChange(module.resource, event.target.checked)
                              }
                            >
                              {t('pages.roles.selectModule')}
                            </Checkbox>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {t('pages.roles.moduleSelectionSummary', {
                                selected: module.selectedCount,
                                total: module.totalCount,
                              })}
                            </Typography.Text>
                          </div>
                        </div>

                        <div className="permission-editor__rows">
                          {module.permissions.map((permission) => {
                            const isDestructive = isDestructivePermissionAction(permission.action);
                            return (
                              <div
                                key={permission.id}
                                className="permission-editor__row"
                                style={{ borderColor: token.colorBorderSecondary }}
                              >
                                <Checkbox
                                  checked={selectedIds.has(permission.id)}
                                  disabled={readOnly}
                                  aria-label={`${module.label}: ${permission.label}`}
                                  onChange={(event) =>
                                    handlePermissionChange(permission.id, event.target.checked)
                                  }
                                >
                                  <span className="permission-editor__permission-copy">
                                    <span className="permission-editor__permission-label">
                                      <Typography.Text
                                        className="permission-editor__permission-label-text"
                                        type={isDestructive ? 'danger' : undefined}
                                      >
                                        {permission.label}
                                      </Typography.Text>
                                      {permission.helpText && (
                                        <Tooltip title={permission.helpText}>
                                          <InfoCircleOutlined
                                            aria-label={t('pages.roles.permissionHelp', {
                                              permission: permission.label,
                                            })}
                                          />
                                        </Tooltip>
                                      )}
                                    </span>
                                    <Typography.Text
                                      type="secondary"
                                      className="permission-editor__permission-key"
                                    >
                                      {permission.name}
                                    </Typography.Text>
                                  </span>
                                </Checkbox>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
